import React, { useMemo, useRef, useState } from 'react';
import Header from './Header';
import { apiService, ChatbotRequest, ChatbotResponse } from '../services/api';
import { Send, Bot, User, RefreshCw } from 'lucide-react';
import { marked } from 'marked';

marked.setOptions({ gfm: true, breaks: true });

type ChatSource = {
  sourceId: string;
  document: string;
  section: string;
  location: string;
  rationale: string;
};

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  citations?: string[];
  sources?: ChatSource[];
};

const DEFAULT_GEMINI_MODEL = 'gemini-rag-finetuned';

function extractCitations(text: string): string[] {
  const matches = text.match(/\[S\d+\]/g) || [];
  return Array.from(new Set(matches));
}

function parseSourcesFromMarkdownTable(text: string): ChatSource[] {
  const lines = text.split('\n').map((line) => line.trim());
  const headerIdx = lines.findIndex((line) =>
    /^\|\s*source id\s*\|/i.test(line)
  );
  if (headerIdx < 0 || headerIdx + 2 >= lines.length) return [];

  const rows: ChatSource[] = [];
  for (let i = headerIdx + 2; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.startsWith('|')) break;
    const cols = line
      .split('|')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (cols.length < 5) continue;
    rows.push({
      sourceId: cols[0],
      document: cols[1],
      section: cols[2],
      location: cols[3],
      rationale: cols[4],
    });
  }
  return rows;
}

function normalizeResponseSources(response: ChatbotResponse): ChatSource[] {
  const raw = Array.isArray(response.sources) ? response.sources : [];
  return raw
    .map((row) => ({
      sourceId: (row.source_id || '').trim(),
      document: (row.document || row.title || '').trim(),
      section: (row.section || row.chunk_id || '').trim(),
      location: (row.url || row.file || '').trim(),
      rationale: (row.rationale || row.why || '').trim(),
    }))
    .filter((row) => row.sourceId || row.document || row.location);
}

function buildRagPrompt(userText: string, forceSourceTable: boolean): string {
  const referenceRules = [
    'You are a references-first oncology assistant.',
    'Use retrieved context when available and do not fabricate citations.',
    'For every factual claim, cite source ids inline like [S1], [S2].',
    'If evidence is missing, explicitly say what is unknown.',
    forceSourceTable
      ? 'At the end, include a markdown table named "Source Traceability" with columns: Source ID | Document | Section/Chunk | URL/File | Why it supports claim.'
      : 'At the end, include a concise references list by source id.',
  ].join(' ');

  return `${userText}\n\n[Assistant behavior rules]\n${referenceRules}`;
}

const ChatbotPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content:
        "Hi — ask me anything. I will prioritize references, cite sources inline, and can produce source-traceability tables.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelId, setModelId] = useState(DEFAULT_GEMINI_MODEL);
  const [showSourceTable, setShowSourceTable] = useState(true);
  const [responseId, setResponseId] = useState<string | undefined>(undefined);
  const endRef = useRef<HTMLDivElement>(null);

  const conversationHistory = useMemo(
    () =>
      messages
        .filter((m) => m.role !== 'assistant' || m.content !== messages[0]?.content)
        .map((m) => ({ role: m.role, content: m.content })),
    [messages]
  );

  const scrollToBottom = () => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || loading) return;

    const nextUser: ChatMessage = { role: 'user', content: text };
    const nextMessages = [...messages, nextUser];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError(null);
    scrollToBottom();

    try {
      const request: ChatbotRequest = {
        query: buildRagPrompt(text, showSourceTable),
        model: modelId.trim() || DEFAULT_GEMINI_MODEL,
        conversation_history: conversationHistory.map((m) => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.content,
        })),
        response_id: responseId,
        page_title: 'RAG Chatbot',
        page_url: window.location.href,
      };
      const response = await apiService.sendChatbotQuery(request);
      if (response.response_id) setResponseId(response.response_id);
      const replyText = response.reply || 'No response.';
      const directSources = normalizeResponseSources(response);
      const parsedSources = directSources.length > 0 ? directSources : parseSourcesFromMarkdownTable(replyText);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: replyText,
          citations: extractCitations(replyText),
          sources: parsedSources,
        },
      ]);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to get response.';
      setError(msg);
      setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${msg}` }]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  return (
    <div className="min-h-screen text-slate-900 bg-slate-50 font-sans">
      <Header />
      <main className="w-full max-w-[1100px] mx-auto px-3 sm:px-4 lg:px-6 py-8">
        <section className="bg-white rounded-2xl border border-slate-200 shadow-card h-[78vh] flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-900">RAG Chatbot</h1>
            <input
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="ml-auto w-[240px] px-3 py-2 text-xs rounded-lg border border-slate-200"
              placeholder="Gemini model id"
            />
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={showSourceTable}
                onChange={(e) => setShowSourceTable(e.target.checked)}
                className="rounded border-slate-300"
              />
              Show source table
            </label>
            <button
              onClick={() => {
                setMessages([messages[0]]);
                setResponseId(undefined);
                setError(null);
              }}
              className="px-3 py-2 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-indigo-600" />
                  </div>
                )}
                <div className="max-w-[85%]">
                  <div
                    className={`rounded-2xl px-4 py-3 text-sm ${
                      m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-700 border border-slate-200'
                    }`}
                  >
                    {m.role === 'assistant' ? (
                      <div
                        className="[&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-slate-200 [&_td]:border [&_td]:border-slate-200 [&_th]:px-2 [&_td]:px-2 [&_th]:py-1 [&_td]:py-1 [&_code]:break-all"
                        dangerouslySetInnerHTML={{ __html: marked.parse(m.content, { async: false }) as string }}
                      />
                    ) : (
                      <div className="inline-flex items-start gap-2">
                        <User className="w-3.5 h-3.5 mt-0.5" />
                        <span>{m.content}</span>
                      </div>
                    )}
                  </div>
                  {m.role === 'assistant' && (m.citations?.length || m.sources?.length) ? (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                    {m.citations && m.citations.length > 0 ? (
                      <p className="mb-2">
                        <span className="font-semibold text-slate-700">Citations:</span>{' '}
                        {m.citations.join(', ')}
                      </p>
                    ) : null}
                    {m.sources && m.sources.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse">
                          <thead>
                            <tr className="bg-slate-50">
                              <th className="border border-slate-200 px-2 py-1 text-left">Source ID</th>
                              <th className="border border-slate-200 px-2 py-1 text-left">Document</th>
                              <th className="border border-slate-200 px-2 py-1 text-left">Section/Chunk</th>
                              <th className="border border-slate-200 px-2 py-1 text-left">URL/File</th>
                              <th className="border border-slate-200 px-2 py-1 text-left">Why it supports claim</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.sources.map((s, sIdx) => (
                              <tr key={`${s.sourceId}-${sIdx}`}>
                                <td className="border border-slate-200 px-2 py-1">{s.sourceId || '—'}</td>
                                <td className="border border-slate-200 px-2 py-1">{s.document || '—'}</td>
                                <td className="border border-slate-200 px-2 py-1">{s.section || '—'}</td>
                                <td className="border border-slate-200 px-2 py-1 break-all">{s.location || '—'}</td>
                                <td className="border border-slate-200 px-2 py-1">{s.rationale || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {loading && <p className="text-sm text-slate-500">Thinking...</p>}
            <div ref={endRef} />
          </div>

          <div className="p-4 border-t border-slate-100">
            {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Ask a question. I will cite sources and can output source tables."
                className="flex-1 px-4 py-3 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="px-4 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-1"
              >
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ChatbotPage;
